/**
 * Message Component
 * Individual chat message display with streaming support
 */

import { useEffect, useState, useMemo, Component, memo } from 'react';
import { Box, Paper, Typography, Alert, CircularProgress, Button, Tooltip, Collapse, IconButton } from '@mui/material';
import { keyframes } from '@mui/system';
import { Person, SmartToy, Download as DownloadIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DataTable from './DataTable';
import ChartDisplay from './ChartDisplay';
import CitationDisplay from './CitationDisplay';
import agentAPI from '../services/api';
import ToolCallCard from './ToolCallCard';
import FeedbackPanel from './FeedbackPanel';
import LoadingIndicator, { ANIMATION_MODE } from './LoadingIndicator';

// Error boundary component for graceful error handling
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <Alert severity="error">Something went wrong displaying this content.</Alert>;
    }
    return this.props.children;
  }
}

// Validate and sanitize data to prevent rendering errors
function validateAndSanitizeData(data) {
  if (!data) return null;

  // If data has the new structure from backend
  if (data.data && Array.isArray(data.data)) {
    return {
      ...data,
      data: data.data.map(row => sanitizeRow(row))
    };
  }

  // If data is already an array (legacy format)
  if (Array.isArray(data)) {
    return data.map(row => sanitizeRow(row));
  }

  return data;
}

// Sanitize individual row values
function sanitizeRow(row) {
  if (!row || typeof row !== 'object') return row;

  const sanitized = {};
  for (const key in row) {
    const value = row[key];
    if (value === null || value === undefined) {
      sanitized[key] = null;
    } else if (typeof value === 'number') {
      // Check for NaN or Infinity
      if (isNaN(value) || !isFinite(value)) {
        sanitized[key] = null;
      } else {
        sanitized[key] = value;
      }
    } else if (typeof value === 'string') {
      // Check for string representations of NaN/Infinity
      if (value === 'NaN' || value === 'Infinity' || value === '-Infinity') {
        sanitized[key] = null;
      } else {
        sanitized[key] = value;
      }
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/** Normalize Unicode dashes, smart quotes, and special whitespace to their
 *  ASCII equivalents so markdown syntax (tables, lists) renders correctly. */
function sanitizeMarkdownText(text) {
  if (!text) return text;
  return text
    // Unicode dashes → ASCII hyphen-minus (fixes table separators & list markers)
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
    // Smart quotes → straight quotes
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    // Non-breaking / special whitespace → regular space
    .replace(/[\u00A0\u2002\u2003\u2009\u200A]/g, ' ');
}

/** Insert newlines before bullet characters that appear mid-line (LLM sometimes omits them)
 *  and convert Unicode bullet chars (•, ·, ‣) to standard markdown list markers so
 *  ReactMarkdown renders them as proper <li> elements.
 *  Table lines (starting with |) are preserved untouched. */
function normalizeBullets(text) {
  if (!text) return text;

  // Split into table vs non-table segments so we never mangle table rows
  const lines = text.split('\n');
  const segments = [];
  let cur = { isTable: false, lines: [] };

  for (const line of lines) {
    const isTable = line.trim().startsWith('|');
    if (isTable !== cur.isTable && cur.lines.length > 0) {
      segments.push(cur);
      cur = { isTable: isTable, lines: [] };
    }
    cur.lines.push(line);
  }
  if (cur.lines.length > 0) segments.push(cur);

  return segments.map(seg => {
    if (seg.isTable) return seg.lines.join('\n');
    let chunk = seg.lines.join('\n');
    // Mid-line Unicode bullets → newline + markdown dash
    chunk = chunk.replace(/([^\n])\s*[•·‣]\s*/g, '$1\n- ');
    // Unicode bullets at line start → markdown dash
    chunk = chunk.replace(/^[•·‣]\s*/gm, '- ');
    return chunk;
  }).join('\n');
}

/** Replace [N] citation markers with markdown links so ReactMarkdown can intercept them. */
function injectCitationLinks(text, hasCitations) {
  if (!hasCitations || !text) return text;
  return text.replace(/\[([1-9]\d?)\]/g, '[\\[$1\\]](#cite-$1)');
}

/** Extract the set of 0-based citation indices actually referenced in the text. */
function getReferencedIndices(text) {
  if (!text) return new Set();
  const matches = [...text.matchAll(/\[([1-9]\d?)\]/g)];
  return new Set(matches.map((m) => parseInt(m[1], 10) - 1));
}

/** Hover tooltip shown when the user mouses over a [N] citation marker. */
function CitationMarker({ citation, index, children }) {
  if (!citation) {
    // No citation data — render plain text
    return <span>{children}</span>;
  }

  const excerpt = (citation.content || '').trim();
  const rawFilename = citation.filename || 'Unknown Source';
  const filename = rawFilename.length > 60 ? rawFilename.slice(0, 59) + '…' : rawFilename;

  const tooltipContent = (
    <Box sx={{ p: 1.5, maxWidth: 480 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
        [{index + 1}] {filename}
      </Typography>
      <Box
        sx={{
          maxHeight: 320,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'grey.400', borderRadius: 2 },
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', lineHeight: 1.6 }}
        >
          "{excerpt}"
        </Typography>
      </Box>
      {citation.url && (
        <Typography
          variant="caption"
          component="a"
          href={citation.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', mt: 0.5, display: 'inline-block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          onClick={(e) => e.stopPropagation()}
        >
          View Source
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      enterDelay={150}
      leaveDelay={100}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 6,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 0,
            maxWidth: 500,
          },
        },
        arrow: { sx: { color: 'background.paper', filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.15))' } },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: 'primary.main',
          fontWeight: 700,
          fontSize: '0.72em',
          verticalAlign: 'super',
          lineHeight: 1,
          px: 0.4,
          py: 0.15,
          borderRadius: '3px',
          border: '1px solid',
          borderColor: 'primary.light',
          '&:hover': { backgroundColor: 'primary.50', textDecoration: 'underline' },
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

const Message = memo(({ message }) => {
  const isUser = message.role === 'user';
  const [displayedContent, setDisplayedContent] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPptDownloading, setIsPptDownloading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [thinkingExpanded, setThinkingExpanded] = useState(false);

  const citations = message.artifacts?.citations;

  // Which [N] markers actually appear in the response text (0-based indices)
  const referencedIndices = useMemo(
    () => getReferencedIndices(displayedContent),
    [displayedContent]
  );

  // Typing animation effect for streaming messages
  useEffect(() => {
    if (message.isStreaming && message.content) {
      // For streaming, display content immediately as it arrives
      setDisplayedContent(message.content);
      setIsTyping(true);
    } else if (!message.isStreaming) {
      // For non-streaming or completed streaming, show full content
      setDisplayedContent(message.content);
      setIsTyping(false);
    }
  }, [message.content, message.isStreaming]);

  // Subtle shimmer gradient sweep across placeholder text (right to left)
  const shimmer = keyframes`
    0%   { background-position: 200% center; }
    100% { background-position: -200% center; }
  `;
  const blink = keyframes`
    0%, 100% { opacity: 0.6; }
    50% { opacity: 0; }
  `;
  const ThinkingBlock = ({ thinkingContent, expanded, onToggle }) => (
    <Box sx={{ mt: 0.5 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 0.5 }}
        onClick={onToggle}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{
            display: 'inline-block',
            fontStyle: 'italic',
            fontWeight: 500,
            letterSpacing: '0.01em',
            ...(ANIMATION_MODE === 'live' ? {
              background: 'linear-gradient(90deg, #8a8a8a 0%, #8a8a8a 40%, #555555 50%, #8a8a8a 60%, #8a8a8a 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: `${shimmer} 4s ease-in-out infinite`,
            } : {
              color: '#8a8a8a',
            }),
          }}
        >
          {ANIMATION_MODE === 'live'
            ? (thinkingContent ? 'Thinking...' : 'Working...')
            : (thinkingContent ? 'Thought process' : 'Working...')}
        </Typography>
        {thinkingContent && (
          <IconButton size="small" sx={{ p: 0, ml: 0.25 }}>
            {expanded ? <ExpandLess sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 16, color: 'text.secondary' }} />}
          </IconButton>
        )}
      </Box>
      {thinkingContent && (
        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 0.75,
              p: 1.5,
              backgroundColor: '#f5f5f5',
              borderLeft: '3px solid #ccc',
              borderRadius: '0 4px 4px 0',
              maxHeight: 200,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'grey.400', borderRadius: 2 },
            }}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: 'text.secondary',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                m: 0,
              }}
            >
              {thinkingContent}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Box>
  );

  // Handle PPT file download
  const handlePptDownload = async () => {
    if (!message.artifacts?.ppt) return;

    setIsPptDownloading(true);
    try {
      const blob = await agentAPI.downloadArtifact('ppt');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename =
        (typeof message.artifacts.ppt === 'object' && message.artifacts.ppt.filename)
          ? message.artifacts.ppt.filename
          : `GIBS_Guru_Presentation_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pptx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PPT download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setIsPptDownloading(false);
    }
  };

  // Handle Excel file download
  const handleExcelDownload = async () => {
    if (!message.artifacts?.excel) return;
    
    setIsDownloading(true);
    try {
      const blob = await agentAPI.downloadArtifact('excel');
      
      // Create download URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Handle both new object format and legacy string format
      let filename;
      if (typeof message.artifacts.excel === 'object' && message.artifacts.excel.filename) {
        filename = message.artifacts.excel.filename;
      } else {
        filename = `GIBS_Guru_Report_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      }
      a.download = filename;
      
      // Trigger download
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download failed:', error);
      // Could add a toast notification here
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1,
      }}
    >
      <Paper
        elevation={isUser ? 1 : 0}
        sx={{
          p: 2,
          maxWidth: '100%',
          width: '100%',
          backgroundColor: isUser ? '#fffbf0' : message.isError ? '#ffebee' : '#fafafa',
          borderRadius: 2,
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {isUser ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
          <Typography variant="caption" sx={{ ml: 1, fontWeight: 'bold' }}>
            {isUser ? 'You' : 'GIBS Guru'}
          </Typography>
          {ANIMATION_MODE === 'live' && (
            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
              {new Date(message.timestamp).toLocaleTimeString()}
            </Typography>
          )}
        </Box>

        {/* Tool call cards */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
            {message.toolCalls.map((tc, i) => (
              <ToolCallCard key={i} toolCall={tc} />
            ))}
          </Box>
        )}

        {message.isError ? (
          <Alert severity="error" sx={{ py: 0 }}>
            {displayedContent || message.content}
          </Alert>
        ) : (
          <Box className="markdown-content">
            {displayedContent ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => {
                    const citeMatch = href?.match(/^#cite-(\d+)$/);
                    if (citeMatch) {
                      const idx = parseInt(citeMatch[1], 10) - 1;
                      return (
                        <CitationMarker citation={citations?.[idx]} index={idx}>
                          {children}
                        </CitationMarker>
                      );
                    }
                    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                  },
                }}
              >
                {injectCitationLinks(normalizeBullets(sanitizeMarkdownText(displayedContent)), citations?.length > 0)}
              </ReactMarkdown>
            ) : null}
            {message.isStreaming && displayedContent.trim() && (
              <LoadingIndicator variant="cursor" color="text.secondary" />
            )}
            {message.isStreaming && !displayedContent.trim() && (
              <ThinkingBlock
                thinkingContent={message.thinkingContent || ''}
                expanded={thinkingExpanded}
                onToggle={() => setThinkingExpanded(prev => !prev)}
              />
            )}
          </Box>
        )}

        {/* Display artifacts if present */}
        {message.artifacts && Object.keys(message.artifacts).length > 0 && (
          <Box sx={{ mt: 2 }}>
            {/* Display data table(s) if present — supports multiple per turn */}
            {message.artifacts.data && (
              <ErrorBoundary fallback={<Alert severity="warning">Unable to display data table. The data may contain invalid values.</Alert>}>
                {(Array.isArray(message.artifacts.data)
                  ? message.artifacts.data
                  : [message.artifacts.data]
                ).map((tableData, idx, arr) => (
                  <DataTable
                    key={idx}
                    data={validateAndSanitizeData(tableData)}
                    title={arr.length > 1 ? `Query Results (${idx + 1})` : 'Query Results'}
                  />
                ))}
              </ErrorBoundary>
            )}
            
            {/* Display Excel download if available */}
            {message.artifacts.excel && (
              <Box sx={{ mt: (message.artifacts.data || message.artifacts.chart || message.artifacts.visualization?.html_content) ? 2 : 0, pt: 2, borderTop: (message.artifacts.data || message.artifacts.chart || message.artifacts.visualization?.html_content) ? '1px solid #eee' : 'none' }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    backgroundColor: 'success.50',
                    border: '1px solid',
                    borderColor: 'success.200',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'success.800' }}>
                        📊 Excel Report Generated
                      </Typography>
                      <Typography variant="caption" color="success.600">
                        {(typeof message.artifacts.excel === 'object' && message.artifacts.excel.filename) 
                          ? message.artifacts.excel.filename 
                          : 'GIBS_Guru_Report.xlsx'}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                      onClick={handleExcelDownload}
                      disabled={isDownloading}
                      sx={{
                        backgroundColor: 'success.600',
                        '&:hover': {
                          backgroundColor: 'success.700',
                        },
                        minWidth: '120px',
                      }}
                    >
                      {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}
            
            {/* Display chart(s) if present — supports multiple charts per response */}
            {(message.artifacts.charts?.length > 0 || message.artifacts.chart || message.artifacts.visualization?.html_content) && (
              <ErrorBoundary fallback={<Alert severity="warning">Unable to display chart. The visualization data may be corrupted.</Alert>}>
                {(message.artifacts.charts ||
                  [message.artifacts.chart || message.artifacts.visualization?.html_content]
                ).filter(Boolean).map((htmlContent, idx) => (
                  <ChartDisplay
                    key={idx}
                    htmlContent={htmlContent}
                    title={message.artifacts.visualizations?.[idx]?.title || "Visualization"}
                    defaultHeight={450}
                  />
                ))}
              </ErrorBoundary>
            )}

            {/* Display PPT download if available */}
            {message.artifacts.ppt && (
              <Box sx={{ mt: 2 }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'secondary.200',
                    borderRadius: 2,
                    backgroundColor: '#fffbf0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                        📑 PowerPoint Presentation Generated
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(typeof message.artifacts.ppt === 'object' && message.artifacts.ppt.filename)
                          ? message.artifacts.ppt.filename
                          : 'GIBS_Guru_Presentation.pptx'}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={isPptDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                      onClick={handlePptDownload}
                      disabled={isPptDownloading}
                      sx={{ minWidth: '120px', backgroundColor: '#2C365E', '&:hover': { backgroundColor: '#1a2240' } }}
                    >
                      {isPptDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Display KB citations if present — only shows referenced ones */}
            {citations && citations.length > 0 && referencedIndices.size > 0 && (
              <CitationDisplay
                citations={citations}
                referencedIndices={referencedIndices}
              />
            )}
          </Box>
        )}

        {/* Feedback panel for assistant messages */}
        {!isUser && !message.isStreaming && displayedContent && (
          <FeedbackPanel messageId={message.id || message.timestamp} />
        )}
      </Paper>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Only re-render if message content or streaming status changes
  return (
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.artifacts === nextProps.message.artifacts &&
    prevProps.message.thinkingContent === nextProps.message.thinkingContent &&
    prevProps.message.toolCalls === nextProps.message.toolCalls
  );
});

Message.displayName = 'Message';

export default Message;